import { useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'

import axiosRequest from 'lib/axios/api-request'

// mirrors QUEUE_CONFIG.EXIZENT_QUEUE.name in server/src/shared/queues/constants.ts
const EXIZENT_QUEUE_NAME = 'exizent'

// mirrors buildExizentSendCaseJobId in server/src/shared/queues/exizent-send-case/index.ts
const buildExizentSendCaseJobId = (probateCaseId: number) =>
  `exizent-send-case-${probateCaseId}`

// BullMQ has no single "pending" state; a job that's retrying sits in delayed
const IN_PROGRESS_STATES = [
  'waiting',
  'active',
  'delayed',
  'prioritized',
  'waiting-children',
]

const POLL_INTERVAL_MS = 2000

const buildQueryKey = (probateCaseId: number) => [
  'exizent-send-case-status',
  probateCaseId,
]

type JobStatus = {
  state: string
  failedReason: string | null
}

const fetchSendCaseStatus = async (
  probateCaseId: number
): Promise<JobStatus | null> => {
  try {
    const { data } = await axiosRequest({
      url: `/api/queues/${EXIZENT_QUEUE_NAME}/jobs/${buildExizentSendCaseJobId(
        probateCaseId
      )}`,
      silentStatuses: [404],
    })
    return data.data.attributes
  } catch (error) {
    // no job means the estate has never been sent, or the job has been cleared
    if ((error as AxiosError).response?.status === 404) return null
    throw error
  }
}

const useEstateQuestionnaireStatus = ({
  probateCaseId,
  externalExizentLastSentAt,
  onProbateEstateUpdate,
}: {
  probateCaseId: number
  externalExizentLastSentAt: Date | null | undefined
  onProbateEstateUpdate?: () => void
}) => {
  // BullMQ clears completed jobs after an hour, so remember the completion
  // while the estate is refetched for its sent date
  const [completedSinceLoad, setCompletedSinceLoad] = useState(false)
  const sentBeforeLoad = !!externalExizentLastSentAt
  const pollEnabled = !sentBeforeLoad && !completedSinceLoad

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery(
    buildQueryKey(probateCaseId),
    () => fetchSendCaseStatus(probateCaseId),
    {
      enabled: pollEnabled,
      refetchInterval: POLL_INTERVAL_MS,
      retry: false,
    }
  )

  const completed = data?.state === 'completed'

  // the next check could be up to 2 seconds away, and might find the job has
  // already finished, so show the send as queued until a check says otherwise
  const markSending = useCallback(() => {
    queryClient.setQueryData<JobStatus>(buildQueryKey(probateCaseId), {
      state: 'waiting',
      failedReason: null,
    })
  }, [queryClient, probateCaseId])

  useEffect(() => {
    if (!completed || completedSinceLoad) return

    setCompletedSinceLoad(true)
    onProbateEstateUpdate?.()
  }, [completed, completedSinceLoad, onProbateEstateUpdate])

  const isReadOnly = sentBeforeLoad || completedSinceLoad || completed

  return {
    isReadOnly,
    markSending,
    loading: isLoading || IN_PROGRESS_STATES.includes(data?.state ?? ''),
    failed: data?.state === 'failed',
  }
}

export default useEstateQuestionnaireStatus
